// src/stores/appraisalStore.js
import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import appraisalService from "@/services/hrm/appraisalService";
import { toast } from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { guardPlanModule } from "@/utils/planAccess";

export const useAppraisalStore = defineStore("appraisals", () => {
  const items = ref([]);
  const item = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const pagination = reactive({ current_page: 1, per_page: 25, total: 0 });

  const filters = reactive({
    search: "",
    status: "",
    date_from: "",
    date_to: "",
    per_page: 25,
    page: 1,
  });

  async function fetchAll(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.HRM_PERFORMANCE))) {
      items.value = [];
      pagination.current_page = 1;
      pagination.per_page = 25;
      pagination.total = 0;
      return;
    }

    loading.value = true; error.value = null;
    try {
      const res = await appraisalService.getAll({ ...filters, ...params });
      const data = res.data;
      items.value = data.data ?? data;
      if (data.meta) {
        pagination.current_page = data.meta.current_page;
        pagination.per_page = data.meta.per_page;
        pagination.total = data.meta.total;
      } else {
        pagination.current_page = 1;
        pagination.per_page = filters.per_page;
        pagination.total = items.value.length;
      }
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch appraisals";
      toast(error.value, "error");
    } finally {
      loading.value = false;
    }
  }

  async function fetchOne(id) {
    loading.value = true; error.value = null;
    try {
      const res = await appraisalService.get(id);
      item.value = res.data;
      return item.value;
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to fetch appraisal";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function create(payload) {
    loading.value = true; error.value = null;
    try {
      await appraisalService.create(payload);
      toast("Appraisal created", "success");
      await fetchAll({ page: 1 });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to create appraisal";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true; error.value = null;
    try {
      await appraisalService.update(id, payload);
      toast("Appraisal updated", "success");
      await fetchAll({ page: pagination.current_page });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to update appraisal";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loading.value = true; error.value = null;
    try {
      await appraisalService.delete(id);
      toast("Appraisal deleted", "success");
      await fetchAll({ page: 1 });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to delete appraisal";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    loading.value = true; error.value = null;
    try {
      await appraisalService.bulkDelete(ids);
      toast("Selected appraisals deleted", "success");
      await fetchAll({ page: 1 });
    } catch (e) {
      error.value = e?.response?.data?.message || "Failed to bulk delete";
      toast(error.value, "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  return {
    items, item, loading, error, pagination, filters,
    fetchAll, fetchOne, create, update, remove, bulkDelete
  };
});
